import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '../../../shared/lib/apiClient';

const RANK = { owner: 3, admin: 2, member: 1 };

const ROLE_OPTIONS = [
  { value: 'owner',  label: 'Owner'  },
  { value: 'admin',  label: 'Admin'  },
  { value: 'member', label: 'Member' },
];

const MemberEditModal = ({ member, members, callerRole, companyRoles = [], onClose, onSave }) => {
  const [jobTitle,      setJobTitle]      = useState(member.jobTitle      || '');
  const [department,    setDepartment]    = useState(member.department    || '');
  const [reportsTo,     setReportsTo]     = useState(member.reportsTo     || '');
  const [role,          setRole]          = useState(member.role);
  const [customRoleId,  setCustomRoleId]  = useState(member.customRoleId  || '');
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState('');
  const overlayRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      // Save tier role + profile fields
      const body = {
        job_title:   jobTitle   || null,
        department:  department || null,
        reports_to:  reportsTo  || null,
        role,
      };
      const res = await apiFetch(`/api/companies/members/${member.userId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to save'); return; }

      // Save custom role assignment separately
      const roleRes = await apiFetch(`/api/companies/members/${member.userId}/custom-role`, {
        method: 'PATCH',
        body: JSON.stringify({ role_id: customRoleId || null }),
      });
      const roleData = await roleRes.json();
      if (!roleRes.ok) { setError(roleData.error || 'Failed to save custom role'); return; }

      const customRole = companyRoles.find(r => r.id === customRoleId);
      onSave({
        ...member,
        jobTitle:       jobTitle       || null,
        department:     department     || null,
        reportsTo:      reportsTo      || null,
        role,
        customRoleId:   customRoleId   || null,
        customRoleName: customRole?.name || null,
      });
      onClose();
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Members the target can report to (exclude self)
  const reportsCandidates = members.filter(m => m.userId !== member.userId);

  // Roles caller can assign (strictly below caller rank)
  const assignableRoles = ROLE_OPTIONS.filter(r => RANK[r.value] < RANK[callerRole]);

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.3)' }}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl shadow-zinc-900/20 w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-100 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-0.5">Edit Member</p>
            <p className="text-sm font-medium text-zinc-900">{member.fullName || member.email?.split('@')[0] || 'Unnamed'}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-100 transition-colors text-zinc-400 hover:text-zinc-700"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M1 1l12 12M13 1L1 13" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="px-4 py-2.5 bg-red-50 text-red-500 text-xs rounded-lg border border-red-100">{error}</div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">Job Title</label>
              <input
                type="text"
                value={jobTitle}
                onChange={e => setJobTitle(e.target.value)}
                placeholder="e.g. Senior Accountant"
                className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 transition-shadow placeholder-zinc-300"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">Department</label>
              <input
                type="text"
                value={department}
                onChange={e => setDepartment(e.target.value)}
                placeholder="e.g. Finance"
                className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 transition-shadow placeholder-zinc-300"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">Reports To</label>
            <select
              value={reportsTo}
              onChange={e => setReportsTo(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg outline-none focus:ring-1 focus:ring-zinc-900 appearance-none transition-shadow"
            >
              <option value="">— No direct manager —</option>
              {reportsCandidates.map(m => (
                <option key={m.userId} value={m.userId}>
                  {m.fullName || 'Unnamed'}{m.jobTitle ? ` · ${m.jobTitle}` : ''}
                </option>
              ))}
            </select>
          </div>

          {assignableRoles.length > 0 && (
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">Tier</label>
              <div className="flex gap-2">
                {assignableRoles.map(r => (
                  <button
                    key={r.value}
                    onClick={() => setRole(r.value)}
                    className={`flex-1 px-3 py-2 text-xs rounded-lg border transition-colors font-medium ${
                      role === r.value
                        ? 'bg-zinc-900 text-white border-zinc-900'
                        : 'bg-zinc-50 text-zinc-600 border-zinc-200 hover:border-zinc-400'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">Custom Role</label>
            <select
              value={customRoleId}
              onChange={e => setCustomRoleId(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg outline-none focus:ring-1 focus:ring-zinc-900 appearance-none transition-shadow"
            >
              <option value="">— No custom role —</option>
              {companyRoles.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            {companyRoles.length === 0 && (
              <p className="text-[10px] text-zinc-300 mt-1">No custom roles yet — create them in the Roles tab</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs text-zinc-500 hover:text-zinc-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-xs rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-50"
          >
            {saving && <span className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />}
            Save changes
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MemberEditModal;
